import { useState } from 'react'
import { Bell, ChevronDown, AlertTriangle, AlertOctagon, Users, Send, Megaphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/hooks/useAuth'
import { sendCriticalNotification, sendWarningNotification, sendNotificationToUsers, sendPositionNotification } from '@/lib/api'
import { usePositions, usePositionUsers } from '@/hooks/usePositions'

type NotificationType = 'warning' | 'critical' | 'users' | 'position' | null

const URGENCY_OPTIONS = [
  { value: '0', label: 'Warning (standard)' },
  { value: '1', label: 'Urgent (high priority)' },
  { value: '2', label: 'Emergency (requires acknowledgement)' },
]

export function NotificationManager() {
  const { hasPermission } = useAuth()
  const { data: usersData } = usePositionUsers()
  const { data: positionsData } = usePositions()
  
  const [notificationType, setNotificationType] = useState<NotificationType>(null)
  const [message, setMessage] = useState('')
  const [notificationTitle, setNotificationTitle] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [selectedPositionId, setSelectedPositionId] = useState('')
  const [urgency, setUrgency] = useState('0')
  const [isSending, setIsSending] = useState(false)
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null)

  const hasAnyNotificationPermission = 
    hasPermission('send_notification') || 
    hasPermission('send_warning') || 
    hasPermission('send_critical_alert')

  const handleOpenNotification = (type: NotificationType) => {
    setNotificationType(type)
    setMessage('')
    setNotificationTitle('')
    setSelectedUsers(new Set())
    setSelectedPositionId('')
    setUrgency('0')
    setSendResult(null)
  }

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const handleSendNotification = async () => {
    if (!message.trim()) return

    setIsSending(true)
    setSendResult(null)

    try {
      let result
      switch (notificationType) {
        case 'warning':
          result = await sendWarningNotification(message, notificationTitle || undefined)
          break
        case 'critical':
          result = await sendCriticalNotification(message, notificationTitle || undefined)
          break
        case 'users':
          if (selectedUsers.size === 0) {
            setSendResult({ success: false, message: 'Please select at least one user' })
            setIsSending(false)
            return
          }
          result = await sendNotificationToUsers(
            Array.from(selectedUsers),
            message,
            notificationTitle || undefined,
            parseInt(urgency)
          )
          break
        case 'position':
          if (!selectedPositionId) {
            setSendResult({ success: false, message: 'Please select a position' })
            setIsSending(false)
            return
          }
          result = await sendPositionNotification(selectedPositionId, message, parseInt(urgency))
          break
      }
      
      if (result?.ok) {
        setSendResult({ success: true, message: 'Notification sent successfully!' })
        setTimeout(() => {
          setNotificationType(null)
          setSendResult(null)
        }, 2000)
      } else {
        setSendResult({ success: false, message: result?.error || 'Failed to send notification' })
      }
    } catch {
      setSendResult({ success: false, message: 'Error sending notification' })
    } finally {
      setIsSending(false)
    }
  }

  const getDialogTitle = () => {
    switch (notificationType) {
      case 'warning':
        return 'Send Warning'
      case 'critical':
        return 'Send Critical Alert'
      case 'users':
        return 'Send to Specific Users'
      case 'position':
        return 'Send Position Notification'
      default:
        return 'Send Notification'
    }
  }

  const getDialogIcon = () => {
    switch (notificationType) {
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'critical':
        return <AlertOctagon className="h-5 w-5 text-red-500" />
      case 'users':
        return <Users className="h-5 w-5 text-blue-500" />
      case 'position':
        return <Megaphone className="h-5 w-5 text-purple-500" />
      default:
        return <Bell className="h-5 w-5" />
    }
  }

  return (
    <>
      {hasAnyNotificationPermission && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Bell className="h-4 w-4" />
              <span className="hidden xl:inline">Notify</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {hasPermission('send_warning') && (
              <DropdownMenuItem onClick={() => handleOpenNotification('warning')}>
                <AlertTriangle className="mr-2 h-4 w-4 text-yellow-500" />
                Warning (Group)
              </DropdownMenuItem>
            )}
            {hasPermission('send_critical_alert') && (
              <DropdownMenuItem onClick={() => handleOpenNotification('critical')}>
                <AlertOctagon className="mr-2 h-4 w-4 text-red-500" />
                Critical (Group)
              </DropdownMenuItem>
            )}
            {(hasPermission('send_warning') || hasPermission('send_critical_alert')) &&
              hasPermission('send_notification') && (
              <DropdownMenuSeparator />
            )}
            {hasPermission('send_notification') && (
              <>
                <DropdownMenuItem onClick={() => handleOpenNotification('users')}>
                  <Users className="mr-2 h-4 w-4 text-blue-500" />
                  Specific Users
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenNotification('position')}>
                  <Megaphone className="mr-2 h-4 w-4 text-purple-500" />
                  Position Notification
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Notification Dialog */}
      <Dialog open={notificationType !== null} onOpenChange={(open) => !open && setNotificationType(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getDialogIcon()}
              {getDialogTitle()}
            </DialogTitle>
            <DialogDescription>
              {notificationType === 'warning' && 'Send a warning notification to the default group.'}
              {notificationType === 'critical' && 'Send a critical alert to the default group.'}
              {notificationType === 'users' && 'Send a notification to specific users.'}
              {notificationType === 'position' && 'Send a notification to all users in a position.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {sendResult && (
              <div className={`text-sm p-3 rounded-md ${
                sendResult.success 
                  ? 'bg-green-500/10 text-green-500' 
                  : 'bg-destructive/10 text-destructive'
              }`}>
                {sendResult.message}
              </div>
            )}

            {notificationType === 'position' && (
              <>
                <div className="space-y-2">
                  <Label>Select Position</Label>
                  <Select value={selectedPositionId} onValueChange={setSelectedPositionId}>
                    <SelectTrigger>
                      <SelectValue placeholder={
                        (positionsData?.positions || []).filter(p => p.users_in_position?.length > 0).length === 0 
                          ? 'No positions with users' 
                          : 'Select a position...'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {(positionsData?.positions || [])
                        .filter(p => p.users_in_position?.length > 0)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.symbol} · {p.status === 'closed' ? 'Closed' : 'Open'} · {p.users_in_position.length} user{p.users_in_position.length !== 1 ? 's' : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Urgency</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {notificationType === 'users' && (
              <>
                <div className="space-y-2">
                  <Label>Select Users</Label>
                  <ScrollArea className="h-[150px] border rounded-md">
                    <div className="p-2 space-y-1">
                      {(usersData?.users || []).length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground text-sm italic">
                          No users available
                        </div>
                      ) : (
                        (usersData?.users || []).map((u) => (
                          <label
                            key={u.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(u.id)}
                              onChange={() => toggleUserSelection(u.id)}
                              disabled={isSending}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <span className="text-sm">
                              @{u.username}
                              {u.pushover_user_key && ' (has Pushover)'}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                  {selectedUsers.size > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Urgency</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {notificationType !== 'position' && (
              <div className="space-y-2">
                <Label htmlFor="notification-title">Title (optional)</Label>
                <Input
                  id="notification-title"
                  placeholder={
                    notificationType === 'warning' ? 'Warning' : 
                    notificationType === 'critical' ? 'CRITICAL ALERT' : 
                    'Notification'
                  }
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notification-message">Message</Label>
              <Textarea
                id="notification-message"
                placeholder="Enter your message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotificationType(null)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendNotification}
              disabled={isSending || !message.trim()}
              className={
                notificationType === 'critical' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : notificationType === 'warning'
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : ''
              }
            >
              <Send className="mr-2 h-4 w-4" />
              {isSending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
